import { useState } from 'react'
import { Explicacao } from '../../../components/Explicacao'
import { cn } from '../../../lib/cn'
import type { ResumoGmud } from '../../../types/gmud'
import { permitirSoltar, type LigacoesDoArraste } from './arraste'
import { CartaoDoQuadro } from './CartaoDoQuadro'
import type { ZonaDoQuadro } from './colunasDoQuadro'

interface Props {
  zona: ZonaDoQuadro
  itens: ResumoGmud[]
  /** Usado como nome da zona quando ela não tem rótulo próprio — a coluna de uma zona só. */
  rotuloDaColuna: string
  ligacoes: LigacoesDoArraste
}

/**
 * Uma faixa da coluna, e o alvo de soltar.
 *
 * Quase toda coluna tem uma zona só e não mostra rótulo nenhum: o título da coluna já diz o que
 * aquilo é. A exceção é a etapa 4, partida em "Aguardando ajustes" e "Aprovada" — dois montes que o
 * anexo põe na mesma etapa e que são notícias opostas.
 *
 * **A zona "Aprovada" não recebe cartão.** Ela nem chama `preventDefault` no arrastar por cima, e
 * por isso o próprio navegador mostra o cursor de proibido antes de qualquer código nosso rodar. A
 * trava é no destino e está declarada em `arraste.ts`, com o motivo: a matriz cresce por INSERT, e
 * travar a origem de hoje deixaria uma linha futura virar o segundo caminho para a aprovação.
 *
 * @param props a zona, os cartões dela e os fios do arraste
 * @returns a faixa com os cartões
 */
export function ZonaDeStatus({ zona, itens, rotuloDaColuna, ligacoes }: Props) {
  const [sobreposta, setSobreposta] = useState(false)
  const nome = zona.rotulo ?? rotuloDaColuna
  const podeReceber = zona.aceitaSoltar && ligacoes.emArraste !== null

  return (
    <div
      onDragOver={(evento) => {
        permitirSoltar(evento, podeReceber)
        setSobreposta(podeReceber)
      }}
      onDragLeave={() => setSobreposta(false)}
      onDrop={(evento) => {
        evento.preventDefault()
        setSobreposta(false)
        // A recusa do navegador (não chamar `preventDefault` no arrastar por cima) já basta num
        // navegador de verdade. Esta linha é a segunda tranca, e ela é barata: a zona que nunca
        // recebe é a da aprovação do comitê, e é a única regra do sistema que vale 10 pontos de IAD.
        if (!zona.aceitaSoltar) {
          return
        }
        ligacoes.aoSoltarNaZona(zona.status, nome)
      }}
      className={cn('rounded-lg p-1', sobreposta && 'bg-niteroi-50 ring-2 ring-inset ring-niteroi-400')}
    >
      {zona.rotulo && (
        <p className="flex items-center gap-1 px-1 pb-1 text-xs font-semibold text-slate-600">
          {zona.rotulo}
          {zona.ajuda && <Explicacao titulo={zona.rotulo} texto={zona.ajuda} />}
        </p>
      )}

      {/* Altura mínima mesmo vazia: sem ela, uma etapa sem cartão nenhum não teria onde receber um
          — e a coluna vazia é justamente para onde o cartão costuma ir. */}
      <ul aria-label={nome} className="min-h-14 space-y-2">
        {itens.map((gmud) => (
          <CartaoDoQuadro key={gmud.id} gmud={gmud} ligacoes={ligacoes} />
        ))}
      </ul>
    </div>
  )
}
